import { CommonModule } from "@angular/common";
import { Component, DestroyRef, ViewChild, computed, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { EzFormComponent } from "@template-ui/components/ez-form/ez-form.component";
import { UiLibraryModule } from "@template-ui/template-ui.module";
import {
  ButtonConfig,
  CrudFilter,
  EzGridDsConfig,
  InputSelectOption,
  PanelHeaderConfig,
} from "@template-ui/types";
import { EzFormInputType, InputConfig } from "@template-ui/types/ez-form.types";
import { ColDef, GridOptions, RowClickedEvent } from "ag-grid-community";
import { ButtonCellRenderer } from "@app/shared/components/tables/ag-grid-cells/button-cell-renderer/button-cell-renderer.component";
import { RoleService } from "@app/shared/services/roles.service";
import {
  LookupMetadataDisplayError,
  LookupMetadataDomainDetail,
  LookupMetadataEntry,
  LookupMetadataEntryCatalogRow,
  LookupMetadataEntryUpsertRequest,
  LookupMetadataFormValues,
} from "./platform-lookup-metadata-admin.models";
import {
  PlatformLookupMetadataAdminService,
  mapLookupMetadataAdminError,
} from "./platform-lookup-metadata-admin.service";

const PLATFORM_ACCESS_ADMIN_READ = "platform_access_admin.read";
const PLATFORM_ACCESS_ADMIN_AUTHOR = "platform_access_admin.author";
const TERMINOLOGY_READ = "terminology.read";

const MODE_OPTIONS: InputSelectOption[] = [
  { value: "inherit", viewValue: "Inherit" },
  { value: "always", viewValue: "Always" },
  { value: "never", viewValue: "Never" },
  { value: "rule", viewValue: "Rule" },
];

@Component({
  selector: "phms-platform-lookup-metadata-admin",
  imports: [CommonModule, UiLibraryModule],
  templateUrl: "./platform-lookup-metadata-admin.component.html",
  styleUrl: "./platform-lookup-metadata-admin.component.scss",
})
export class PlatformLookupMetadataAdminComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly service = inject(PlatformLookupMetadataAdminService);
  private readonly roles = inject(RoleService);
  private readonly router = inject(Router);

  @ViewChild("entryForm")
  private entryForm?: EzFormComponent;

  private readonly canReadPlatformMetadata = this.roles.hasAnyPermissionSignal(PLATFORM_ACCESS_ADMIN_READ);
  private readonly canReadTerminology = this.roles.hasAnyPermissionSignal(TERMINOLOGY_READ);
  readonly canRead = computed(() => this.canReadPlatformMetadata() && this.canReadTerminology());
  readonly canAuthor = this.roles.hasAnyPermissionSignal(PLATFORM_ACCESS_ADMIN_AUTHOR);
  readonly isSaving = signal(false);
  readonly selectedDomain = signal<LookupMetadataDomainDetail | null>(null);
  readonly selectedEntry = signal<LookupMetadataEntry | null>(null);
  readonly isCreating = signal(false);
  readonly successMessage = signal<string | null>(null);
  readonly errorState = signal<LookupMetadataDisplayError | null>(null);
  readonly catalogFilterOptions = signal<InputSelectOption[]>([]);
  readonly entryGridReloadTrigger = signal(0);
  readonly entryColumnDefs: ColDef[] = this.buildEntryColumnDefs();
  // Mirrors the ui-grid defaults but turns on column resizing so admins can widen cramped columns.
  readonly entryGridDefaultColDef: ColDef = {
    flex: 1,
    minWidth: 150,
    floatingFilter: false,
    suppressHeaderFilterButton: true,
    resizable: true,
    suppressMovable: true,
    cellStyle: {
      display: "block",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
  };
  readonly entryAgGridOptions: GridOptions<LookupMetadataEntryCatalogRow> = {
    rowSelection: "single",
    onRowClicked: (event) => this.selectCatalogRow(event),
  };
  readonly entryPanelHeaderConfig: PanelHeaderConfig = this.buildEntryPanelHeaderConfig();
  readonly pagePanelSubtitle = "Legacy terminology rows are retained here for compatibility and cross-reference only. Authoring, impact review, publication, restore, history, and health are managed in Terminology.";

  // Human-readable detail header. The title shows the friendly display text + "Term" (e.g.
  // "Intake Term") instead of the raw token code, and the subtitle collapses the redundant
  // "terminology / terminology" domain path down to just the catalog label ("Terminology").
  readonly detailPanelTitle = computed(() => {
    if (this.isCreating()) {
      return "New Term";
    }
    const entry = this.selectedEntry();
    if (!entry) {
      return "Metadata Entry Detail";
    }
    const display = (entry.displayText ?? "").trim();
    return display ? `${display} Term` : entry.code;
  });

  readonly detailSubtitle = computed(() => {
    const domain = this.selectedDomain();
    return domain ? toCatalogLabel(domain.catalogTypeCode) : "Select an entry from the catalog.";
  });
  readonly pagePanelHeaderConfig: PanelHeaderConfig = {
    additionalActions: [
      {
        name: "refreshCatalog",
        type: EzFormInputType.BUTTON,
        buttonText: "Refresh",
        disabled: () => this.isSaving(),
      },
    ],
    additionalActionsContainerClass: "gap-2 items-center compact",
  };

  readonly entryEditBlockedReason = computed(() => {
    if (this.isCreating()) {
      return null;
    }

    const entry = this.selectedEntry();
    if (!entry) {
      return "Select an entry before editing.";
    }

    if (normalizeCode(entry.catalogTypeCode) === "terminology") {
      return "This legacy terminology row is read-only. Open its versioned definition in Terminology to author wording or manage its lifecycle.";
    }

    const status = normalizeCode(entry.versionStatusCode);
    return status === "draft"
      ? null
      : "Only draft configuration versions can be edited here.";
  });
  readonly isSelectedTerminology = computed(() =>
    normalizeCode(this.selectedEntry()?.catalogTypeCode) === "terminology",
  );
  readonly canOpenSelectedTerminology = computed(() =>
    this.canReadTerminology() && this.isSelectedTerminology(),
  );
  readonly entryFormActionButtons = computed<ButtonConfig[]>(() => [
    {
      id: "saveEntry",
      buttonText: this.isSaving() ? "Saving..." : (this.isCreating() ? "Create Entry" : "Save Entry"),
      disabled: !this.canAuthor() || this.isSaving() || !!this.entryEditBlockedReason(),
    },
    {
      id: "resetForm",
      buttonText: "Reset",
      severity: "secondary",
      disabled: this.isSaving(),
    },
  ]);
  readonly entryGridConfig: EzGridDsConfig = this.buildEntryGridConfig();

  entryFormConfig: InputConfig[] = this.buildFormConfig(null);
  entryFormValues: LookupMetadataFormValues = this.createEmptyFormValues();

  constructor() {
    this.loadCatalogFilterOptions();
  }

  onPagePanelHeaderButtonClick(event: { name?: string }): void {
    switch (event.name) {
      case "refreshCatalog":
        this.loadCatalogFilterOptions();
        this.entryGridReloadTrigger.update(n => n + 1);
        break;
      case "beginCreateEntry":
        this.beginCreate();
        break;
      default:
        break;
    }
  }

  private loadCatalogFilterOptions(): void {
    this.service.listDomains()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (domains) => {
          const options = new Map<string, InputSelectOption>();
          domains.forEach((domain) => {
            const normalizedCatalog = normalizeCode(domain.catalogTypeCode);
            if (normalizedCatalog && !options.has(normalizedCatalog)) {
              options.set(normalizedCatalog, {
                value: domain.catalogTypeCode,
                viewValue: toCatalogLabel(domain.catalogTypeCode),
              });
            }
          });

          this.catalogFilterOptions.set(
            [...options.values()].sort((left, right) => left.viewValue.localeCompare(right.viewValue)),
          );
        },
        error: (error) => {
          this.errorState.set(mapLookupMetadataAdminError(error));
        },
      });
  }

  onEntryFormActionButtonClick(button: ButtonConfig): void {
    switch (button.id) {
      case "saveEntry":
        this.saveEntry();
        break;
      case "resetForm":
        this.resetForm();
        break;
      default:
        break;
    }
  }

  beginCreate(clearMessages = true): void {
    if (clearMessages) {
      this.clearMessages();
    }

    this.isCreating.set(true);
    this.selectedEntry.set(null);
    this.entryFormValues = this.createEmptyFormValues();
    this.entryFormConfig = this.buildFormConfig(this.selectedDomain()?.catalogTypeCode ?? null);
    this.entryForm?.clearErrors();
  }

  editEntry(entry: LookupMetadataEntry, clearMessages = true): void {
    if (clearMessages) {
      this.clearMessages();
    }

    this.isCreating.set(false);
    this.selectedEntry.set(entry);
    this.entryFormValues = this.mapEntryToFormValues(entry);
    this.entryFormConfig = this.buildFormConfig(entry.catalogTypeCode);
    this.entryForm?.clearErrors();
  }

  saveEntry(): void {
    const domain = this.selectedDomain();
    if (!domain) {
      return;
    }

    if (!this.canAuthor()) {
      this.errorState.set({
        title: "Access denied",
        messages: ["You do not have permission to author lookup metadata."],
        issueCodes: [],
      });
      return;
    }

    const blockedReason = this.entryEditBlockedReason();
    if (blockedReason) {
      this.errorState.set({
        title: "Entry is read-only",
        messages: [blockedReason],
        issueCodes: [],
      });
      return;
    }

    if (!this.ensureFormIsValid()) {
      return;
    }

    const request = this.buildRequest(domain, this.entryFormValues);
    if (!request) {
      return;
    }

    this.clearMessages();
    this.isSaving.set(true);
    const selectedEntry = this.selectedEntry();
    const save$ = this.isCreating() || !selectedEntry
      ? this.service.createEntry(request)
      : this.service.updateEntry(selectedEntry.catalogTypeCode, selectedEntry.id, request);

    save$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (entry) => {
          this.isSaving.set(false);
          this.successMessage.set(this.isCreating() ? "Catalog entry created." : "Catalog entry updated.");
          this.applySavedEntry(entry);
          this.entryGridReloadTrigger.update(n => n + 1);
        },
        error: (error) => {
          this.isSaving.set(false);
          this.errorState.set(mapLookupMetadataAdminError(error));
        },
      });
  }

  resetForm(): void {
    this.errorState.set(null);
    this.entryForm?.clearErrors();
    const selectedEntry = this.selectedEntry();
    this.entryFormValues = selectedEntry ? this.mapEntryToFormValues(selectedEntry) : this.createEmptyFormValues();
  }

  openSelectedTerminology(): void {
    const entry = this.selectedEntry();
    if (!entry || normalizeCode(entry.catalogTypeCode) !== "terminology") {
      return;
    }

    if (!this.canReadTerminology()) {
      this.errorState.set({
        title: "Access denied",
        messages: ["You do not have permission to open the terminology workspace."],
        issueCodes: [],
      });
      return;
    }

    void this.router.navigate(["/platform-admin/terminology"], {
      queryParams: {
        token: entry.code,
        configuration: entry.configurationCode,
        view: "edit",
      },
    });
  }

  toStatusLabel(statusCode: string | null | undefined): string {
    const normalized = normalizeCode(statusCode);
    return normalized
      ? normalized.split("_").map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1)).join(" ")
      : "Unknown";
  }

  private selectCatalogRow(event: RowClickedEvent<LookupMetadataEntryCatalogRow>): void {
    if (event.data) {
      this.editEntry(this.mapCatalogRowToEntry(event.data));
    }
  }

  private buildEntryColumnDefs(): ColDef[] {
    return [
      { field: "id", headerName: "Legacy ID", width: 100 },
      { field: "code", headerName: "Token", flex: 2, minWidth: 240 },
      { field: "displayText", headerName: "Published Wording", flex: 2, minWidth: 200 },
      {
        headerName: "Scope",
        minWidth: 160,
        valueGetter: ({ data }) => data?.scopeKey
          ? `${data.scopeTypeCode} / ${data.scopeKey}`
          : data?.scopeTypeCode,
      },
      { field: "configurationCode", headerName: "Configuration", minWidth: 170 },
      { field: "versionCode", headerName: "Version", width: 110 },
      {
        field: "versionStatusCode",
        headerName: "Status",
        width: 110,
        valueFormatter: ({ value }) => this.toStatusLabel(value as string),
      },
      {
        field: "isActive",
        headerName: "Active",
        width: 90,
        valueFormatter: ({ value }) => value ? "Yes" : "No",
      },
      {
        headerName: "",
        width: 90,
        maxWidth: 90,
        sortable: false,
        resizable: false,
        // Pin the Select action to the right edge so it stays visible even when the wide catalog
        // scrolls horizontally — the user must always be able to open an entry.
        pinned: "right",
        lockPinned: true,
        cellRenderer: ButtonCellRenderer,
        cellRendererParams: {
          label: "Open",
          onClick: (params: { rowData: LookupMetadataEntryCatalogRow }) => {
            this.editEntry(this.mapCatalogRowToEntry(params.rowData));
            this.openSelectedTerminology();
          },
        },
      },
    ];
  }

  private buildEntryGridConfig(): EzGridDsConfig {
    return {
      datasourceId: "lookupMetadataEntries",
      bindings: [
        {
          bindingType: "READ",
          endpoint: this.service.getODataEndpoint(),
          protocol: "GET_ODATA",
        },
      ],
      colDefs: this.entryColumnDefs,
      // Always-on constraint: this page now shows terminology entries only. staticFilters are
      // enforced on every load and cannot be cleared, producing `catalogTypeCode eq 'terminology'`.
      staticFilters: {
        catalogTypeCode: {
          name: "catalogTypeCode",
          value: "terminology",
        } as CrudFilter,
      },
    };
  }

  private buildEntryPanelHeaderConfig(): PanelHeaderConfig {
    // Filter/preset dropdown menus removed at the user's request — the cramped Clear Filters /
    // Auto-apply / Manage Presets / preset dropdown / Export cluster and the Catalog/Domain/Code/…
    // filter inputs. The catalog keeps just its title; sort the columns to find entries.
    return {
      panelHeaderTitle: "Legacy terminology index",
      titleClickToReload: true,
    };
  }

  private mapCatalogRowToEntry(row: LookupMetadataEntryCatalogRow): LookupMetadataEntry {
    this.selectedDomain.set(this.mapCatalogRowToDomain(row));
    return {
      id: row.id,
      catalogTypeCode: row.catalogTypeCode,
      domainCode: row.domainCode,
      configurationCode: row.configurationCode,
      versionCode: row.versionCode,
      configurationVersionId: row.configurationVersionId,
      versionStatusCode: row.versionStatusCode,
      code: row.code,
      displayText: row.displayText,
      isActive: row.isActive,
      effectiveStartUtc: row.effectiveStartUtc,
      effectiveEndUtc: row.effectiveEndUtc,
      scopeTypeCode: row.scopeTypeCode,
      scopeKey: row.scopeKey,
      sortOrder: row.sortOrder,
      metadata: {
        entityCode: row.entityCode,
        requiredModeCode: row.requiredModeCode,
        visibleModeCode: row.visibleModeCode,
        editableModeCode: row.editableModeCode,
        ruleCode: row.ruleCode,
        clientHintCode: row.clientHintCode,
        categoryCode: row.categoryCode,
        severityCode: row.severityCode,
        clientHandlingHintCode: row.clientHandlingHintCode,
        subjectCode: row.subjectCode,
        bindingTypeCode: row.bindingTypeCode,
        expressionJson: row.expressionJson,
        isDefault: row.isDefault ? "true" : "false",
      },
    };
  }

  private mapCatalogRowToDomain(row: LookupMetadataEntryCatalogRow): LookupMetadataDomainDetail {
    return {
      catalogTypeCode: row.catalogTypeCode,
      domainCode: row.domainCode,
      displayName: `${row.catalogTypeCode} / ${row.domainCode}`,
      description: null,
      configurationCode: row.configurationCode,
      entryCount: 0,
      entries: [],
    };
  }

  private buildFormConfig(catalogTypeCode: string | null): InputConfig[] {
    const readOnly = !!this.entryEditBlockedReason();
    const config: InputConfig[] = [
      {
        name: "configurationVersionId",
        type: EzFormInputType.NUMBER,
        label: "Configuration Version ID",
        row: 1,
        width: "1x",
        readOnly: !this.isCreating(),
        validation: [Validators.required, Validators.min(1)],
      },
      {
        name: "code",
        type: EzFormInputType.TEXT,
        label: "Code",
        row: 1,
        width: "1x",
        readOnly,
        validation: [Validators.required],
        // Breathing room below each row group so the form isn't cramped.
        sectionSpacer: { after: [{ spacingScale: 2 }] },
      },
      {
        name: "displayText",
        type: EzFormInputType.TEXTAREA,
        label: "Display Text",
        row: 2,
        width: "fill",
        readOnly,
        validation: [Validators.required],
        textAreaRows: 3,
        sectionSpacer: { after: [{ spacingScale: 2 }] },
      },
      {
        name: "sortOrder",
        type: EzFormInputType.NUMBER,
        label: "Sort Order",
        row: 3,
        width: "1x",
        readOnly,
        validation: [Validators.required, Validators.min(0)],
      },
      {
        name: "isActive",
        type: EzFormInputType.CHECKBOX,
        label: activeLabel(catalogTypeCode),
        row: 3,
        width: "1x",
        readOnly,
        sectionSpacer: { after: [{ spacingScale: 2 }] },
      },
      {
        name: "scopeTypeCode",
        type: EzFormInputType.TEXT,
        label: "Scope Type Code",
        row: 4,
        width: "1x",
        readOnly,
      },
      {
        name: "scopeKey",
        type: EzFormInputType.TEXT,
        label: "Scope Key",
        row: 4,
        width: "1x",
        readOnly,
      },
    ];

    switch (normalizeCode(catalogTypeCode)) {
      case "field-metadata":
        return [
          ...config,
          {
            name: "entityCode",
            type: EzFormInputType.TEXT,
            label: "Entity Code",
            row: 5,
            width: "1x",
            readOnly,
            validation: [Validators.required],
          },
          {
            name: "requiredModeCode",
            type: EzFormInputType.SELECT,
            label: "Required Mode",
            row: 6,
            width: "1x",
            readOnly,
            options: MODE_OPTIONS,
          },
          {
            name: "visibleModeCode",
            type: EzFormInputType.SELECT,
            label: "Visible Mode",
            row: 6,
            width: "1x",
            readOnly,
            options: MODE_OPTIONS,
          },
          {
            name: "editableModeCode",
            type: EzFormInputType.SELECT,
            label: "Editable Mode",
            row: 6,
            width: "1x",
            readOnly,
            options: MODE_OPTIONS,
          },
          {
            name: "ruleCode",
            type: EzFormInputType.TEXT,
            label: "Rule Code",
            row: 7,
            width: "1x",
            readOnly,
          },
          {
            name: "clientHintCode",
            type: EzFormInputType.TEXT,
            label: "Client Hint Code",
            row: 7,
            width: "1x",
            readOnly,
          },
        ];
      case "issue-catalog":
        return [
          ...config,
          {
            name: "categoryCode",
            type: EzFormInputType.TEXT,
            label: "Category Code",
            row: 5,
            width: "1x",
            readOnly,
            validation: [Validators.required],
          },
          {
            name: "severityCode",
            type: EzFormInputType.TEXT,
            label: "Severity Code",
            row: 5,
            width: "1x",
            readOnly,
            validation: [Validators.required],
          },
          {
            name: "clientHandlingHintCode",
            type: EzFormInputType.TEXT,
            label: "Client Handling Hint",
            row: 6,
            width: "1x",
            readOnly,
            validation: [Validators.required],
          },
          {
            name: "ruleCode",
            type: EzFormInputType.TEXT,
            label: "Rule Code",
            row: 6,
            width: "1x",
            readOnly,
          },
        ];
      case "terminology":
        return config.map((input) => ({
          ...input,
          label: input.name === "code" ? "Token Code" : input.label,
          readOnly: true,
        }));
      case "rule-bindings":
        return [
          ...config,
          {
            name: "subjectCode",
            type: EzFormInputType.TEXT,
            label: "Subject Code",
            row: 5,
            width: "1x",
            readOnly,
            validation: [Validators.required],
          },
          {
            name: "bindingTypeCode",
            type: EzFormInputType.TEXT,
            label: "Binding Type Code",
            row: 5,
            width: "1x",
            readOnly,
            validation: [Validators.required],
          },
          {
            name: "expressionJson",
            type: EzFormInputType.TEXTAREA,
            label: "Expression JSON",
            row: 6,
            width: "fill",
            readOnly,
            textAreaRows: 4,
          },
        ];
      default:
        return [
          ...config,
          {
            name: "isDefault",
            type: EzFormInputType.CHECKBOX,
            label: "Default Value",
            row: 5,
            width: "1x",
            readOnly,
          },
        ];
    }
  }

  private createEmptyFormValues(): LookupMetadataFormValues {
    return {
      configurationVersionId: null,
      code: "",
      displayText: "",
      isActive: true,
      effectiveStartUtc: new Date(),
      effectiveEndUtc: null,
      scopeTypeCode: "global",
      scopeKey: "",
      sortOrder: 0,
      entityCode: "",
      requiredModeCode: "inherit",
      visibleModeCode: "inherit",
      editableModeCode: "inherit",
      ruleCode: "",
      clientHintCode: "",
      categoryCode: "",
      severityCode: "",
      clientHandlingHintCode: "",
      subjectCode: "",
      bindingTypeCode: "",
      expressionJson: "",
      isDefault: false,
    };
  }

  private mapEntryToFormValues(entry: LookupMetadataEntry): LookupMetadataFormValues {
    return {
      ...this.createEmptyFormValues(),
      configurationVersionId: entry.configurationVersionId,
      code: entry.code,
      displayText: entry.displayText,
      isActive: entry.isActive,
      effectiveStartUtc: entry.effectiveStartUtc ? new Date(entry.effectiveStartUtc) : null,
      effectiveEndUtc: entry.effectiveEndUtc ? new Date(entry.effectiveEndUtc) : null,
      scopeTypeCode: entry.scopeTypeCode ?? "global",
      scopeKey: entry.scopeKey ?? "",
      sortOrder: entry.sortOrder,
      entityCode: entry.metadata?.["entityCode"] ?? "",
      requiredModeCode: entry.metadata?.["requiredModeCode"] ?? "inherit",
      visibleModeCode: entry.metadata?.["visibleModeCode"] ?? "inherit",
      editableModeCode: entry.metadata?.["editableModeCode"] ?? "inherit",
      ruleCode: entry.metadata?.["ruleCode"] ?? "",
      clientHintCode: entry.metadata?.["clientHintCode"] ?? "",
      categoryCode: entry.metadata?.["categoryCode"] ?? "",
      severityCode: entry.metadata?.["severityCode"] ?? "",
      clientHandlingHintCode: entry.metadata?.["clientHandlingHintCode"] ?? "",
      subjectCode: entry.metadata?.["subjectCode"] ?? "",
      bindingTypeCode: entry.metadata?.["bindingTypeCode"] ?? "",
      expressionJson: entry.metadata?.["expressionJson"] ?? "",
      isDefault: normalizeCode(entry.metadata?.["isDefault"]) === "true",
    };
  }

  private buildRequest(
    domain: LookupMetadataDomainDetail,
    values: LookupMetadataFormValues,
  ): LookupMetadataEntryUpsertRequest | null {
    const effectiveStartUtc = toIsoUtc(values.effectiveStartUtc);
    const effectiveEndUtc = toIsoUtc(values.effectiveEndUtc);
    if (!effectiveStartUtc) {
      this.errorState.set({
        title: "Validation failed",
        messages: ["Effective start must be a valid date and time."],
        issueCodes: [],
      });
      return null;
    }

    return {
      catalogTypeCode: domain.catalogTypeCode,
      domainCode: domain.domainCode,
      configurationVersionId: Number(values.configurationVersionId ?? 0),
      code: values.code.trim(),
      displayText: values.displayText.trim(),
      isActive: values.isActive,
      effectiveStartUtc,
      effectiveEndUtc,
      scopeTypeCode: values.scopeTypeCode.trim() || "global",
      scopeKey: nullableText(values.scopeKey),
      sortOrder: Number(values.sortOrder ?? 0),
      metadata: {
        entityCode: nullableText(values.entityCode),
        requiredModeCode: nullableText(values.requiredModeCode),
        visibleModeCode: nullableText(values.visibleModeCode),
        editableModeCode: nullableText(values.editableModeCode),
        ruleCode: nullableText(values.ruleCode),
        clientHintCode: nullableText(values.clientHintCode),
        categoryCode: nullableText(values.categoryCode),
        severityCode: nullableText(values.severityCode),
        clientHandlingHintCode: nullableText(values.clientHandlingHintCode),
        subjectCode: nullableText(values.subjectCode),
        bindingTypeCode: nullableText(values.bindingTypeCode),
        expressionJson: nullableText(values.expressionJson),
        isDefault: values.isDefault ? "true" : "false",
      },
    };
  }

  private applySavedEntry(entry: LookupMetadataEntry): void {
    this.selectedDomain.set(this.mapEntryToDomain(entry));
    this.editEntry(entry, false);
  }

  private mapEntryToDomain(entry: LookupMetadataEntry): LookupMetadataDomainDetail {
    return {
      catalogTypeCode: entry.catalogTypeCode,
      domainCode: entry.domainCode,
      displayName: `${entry.catalogTypeCode} / ${entry.domainCode}`,
      description: null,
      configurationCode: entry.configurationCode,
      entryCount: 0,
      entries: [],
    };
  }

  private ensureFormIsValid(): boolean {
    const formGroup = this.entryForm?.theFormGroup;
    if (!formGroup || formGroup.valid) {
      return true;
    }

    formGroup.markAllAsTouched();
    this.errorState.set({
      title: "Validation failed",
      messages: [...new Set(this.entryForm?.controlErrors?.map((entry) => entry.message) ?? ["Resolve the form errors before saving."])],
      issueCodes: [],
    });
    return false;
  }

  private clearMessages(): void {
    this.successMessage.set(null);
    this.errorState.set(null);
  }
}

function activeLabel(catalogTypeCode: string | null): string {
  return normalizeCode(catalogTypeCode) === "issue-catalog" ? "Blocking" : "Active";
}

function toCatalogLabel(catalogTypeCode: string): string {
  return catalogTypeCode
    .split("-")
    .filter((segment) => segment.length > 0)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ");
}

function normalizeCode(value: string | null | undefined): string {
  return (value ?? "").trim().toLowerCase();
}

function nullableText(value: string | null | undefined): string | null {
  const normalized = (value ?? "").trim();
  return normalized.length > 0 ? normalized : null;
}

function toIsoUtc(value: string | Date | null): string | null {
  if (!value) {
    return null;
  }

  const candidate = value instanceof Date ? value : new Date(value);
  return Number.isNaN(candidate.getTime()) ? null : candidate.toISOString();
}
